import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../shared/api/httpClient';
import { useCountryStore } from '../../shared/country/countryStore';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { authErrorMessage } from './authShared';
import { beginGoogleBridge, prepareProof } from './googleBridge';
import { CountryPicker } from './country/CountryPicker';
import { RegisterCountryNotice } from './RegisterCountryNotice';
import { RegistrationChallenge } from './RegistrationChallenge';
import type { GoogleConfig } from './firebaseGoogle';

type Prepared = Awaited<ReturnType<typeof prepareProof>>;
type Registration = { turnstileToken: string; acceptedTerms: boolean; acceptedMarketing: boolean; inviteCode: string; onAttempt: () => void };
export function GoogleSignIn({ onAuthenticated, remember, registration }: {
  onAuthenticated: () => void; remember: boolean; registration?: Registration;
}) {
  const t = useI18nStore(s => s.t);
  const language = useI18nStore(s => s.language);
  const country = useCountryStore(s => s.country);
  const marketAvailable = useCountryStore(s => s.loaded && s.countries.length > 0);
  const config = useQuery({ queryKey: ['google-login-config'], queryFn: () => apiFetch<GoogleConfig>('/api/auth/google/config'), staleTime: 30000, retry: false });
  const [expanded, setExpanded] = useState(false);
  const [token, setToken] = useState('');
  const [terms, setTerms] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [prepared, setPrepared] = useState<Prepared | null>(null);
  const [error, setError] = useState<unknown>(null);
  const pending = useRef(false);
  const mounted = useRef(true);
  const cancellation = useRef<AbortController | null>(null);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; cancellation.current?.abort(); }; }, []);
  const options = registration || { turnstileToken: token, acceptedTerms: terms, acceptedMarketing: false, inviteCode: '' };
  const eligible = marketAvailable && options.acceptedTerms && !!options.turnstileToken;

  useEffect(() => {
    if (!expanded || !config.data?.enabled) return;
    let cancelled = false;
    setPrepared(null);
    void prepareProof().then(proof => {
      if (cancelled) return;
      setPrepared(proof);
    }).catch(err => { if (!cancelled) setError(err); });
    return () => { cancelled = true; };
  }, [expanded, config.data, attempt]);

  async function login() {
    if (!eligible || !prepared || !config.data?.authOrigin || pending.current) return;
    pending.current = true; setBusy(true); setError(null);
    try {
      const query = new URLSearchParams(window.location.search);
      const hashQuery = new URLSearchParams(window.location.hash.split('?')[1] || '');
      const invite = options.inviteCode || query.get('invite') || query.get('ref') || hashQuery.get('invite') || hashQuery.get('ref') || '';
      cancellation.current = new AbortController();
      const completed = await beginGoogleBridge(prepared, { countryCode: country.code, locale: language,
        invite_code: invite, accepted_terms: options.acceptedTerms, accepted_marketing: options.acceptedMarketing, turnstileToken: options.turnstileToken }, remember, config.data.authOrigin, cancellation.current.signal);
      if (!mounted.current || !completed) return;
      onAuthenticated();
    } catch (err) {
      if (mounted.current) { setError(err); setToken(''); registration?.onAttempt(); setAttempt(v => v + 1); }
    } finally { cancellation.current = null; pending.current = false; if (mounted.current) setBusy(false); }
  }

  if (!config.data?.enabled) return null;
  return <div className="mt-5 pt-3">
    <div className="auth-social-divider">{t('authSocialDivider')}</div>
    {!expanded ? <button type="button" className="w-full rounded-xl bg-white text-gray-900 font-semibold py-3" onClick={() => setExpanded(true)}>{t('googleContinue')}</button> : <>
      {!registration && <>
        <div className="flex items-center gap-2"><CountryPicker showPhonePrefix={false} /><RegisterCountryNotice /></div>
        <label className="flex items-start gap-2 mt-3 text-xs text-ink-2"><input type="checkbox" className="auth-checkbox" checked={terms} onChange={e => setTerms(e.target.checked)} /><span>{t('agreeTermsLabelPrefix')}<a href="/termos.html" target="_blank" rel="noopener" className="underline">{t('agreeTermsLink')}</a></span></label>
        <RegistrationChallenge onToken={setToken} attempt={attempt} />
      </>}
      {(!eligible || !prepared || busy) && <p className="text-xs text-ink-3 mb-2" role="status">{t(busy ? 'authSigningIn' : !eligible ? 'googleCompleteChecks' : 'verificationLoading')}</p>}
      <button type="button" disabled={!eligible || !prepared || busy} onClick={() => void login()} className="w-full rounded-xl bg-white text-gray-900 font-semibold py-3 disabled:opacity-45">{t('googleContinue')}</button>
      {error != null && <div role="alert" className="text-xs text-rose-400 mt-2"><p>{String((error as Error).message) === 'GOOGLE_EMAIL_EXISTS' ? t('googleEmailExists') : authErrorMessage(error, t, 'email', 'googleFailed')}</p><button type="button" className="underline mt-2" disabled={busy} onClick={() => { setError(null); setAttempt(v => v + 1); }}>{t('googleRetry')}</button></div>}
    </>}
  </div>;
}
